import { useTranslations } from 'use-intl'
import { Button } from '@heroui/react'
import { MapPin, CalendarClock } from 'lucide-react'
import { toast } from 'sonner'

interface Location {
  x: number // longitude
  y: number // latitude
}

interface ShopActionBarProps {
  location: Location | null
  tabelogUrl: string | null
  rawName?: string | null
  cleanName?: string | null
  aliasName?: string | null
  address?: string | null
}

export function ShopActionBar({ location, tabelogUrl, rawName, cleanName, aliasName, address }: ShopActionBarProps) {
  const t = useTranslations('shopDetail')

  // 1. Try to extract Japanese original name inside parentheses from rawName
  const match = rawName ? rawName.match(/[（(]([^）)]+)[）)]\s*$/) : null
  const detectedOriginalName = match ? match[1].trim() : null

  // 2. Resolve final shop name: Extracted Original > Alias > Clean Name
  const queryName = detectedOriginalName || aliasName || cleanName || ''

  const openGoogleMaps = () => {
    // 3. Fallback to coordinate search if no name is resolved (unlikely), otherwise do high-precision place search
    if (queryName) {
      // Append address for local boundary resolution (prevents selecting wrong chain stores in different regions)
      const queryText = encodeURIComponent(`${queryName} ${address || ''}`)
      const url = `https://www.google.com/maps/search/?api=1&query=${queryText}`
      window.open(url, '_blank')
    } else if (location) {
      // PostGIS points are (lon, lat) i.e. (x, y)
      const url = `https://www.google.com/maps/search/?api=1&query=${location.y},${location.x}`
      window.open(url, '_blank')
    }
  }

  const openTabelog = () => {
    if (tabelogUrl) {
      toast.info(t('tabelogWarning'), {
        duration: 3000,
      })
      setTimeout(() => {
        window.open(tabelogUrl, '_blank')
      }, 1500)
    }
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 pb-safe bg-surface-container-lowest/90 backdrop-blur-xl shadow-[0_-10px_40px_rgba(0,0,0,0.05)] border-t border-outline-variant/10 lg:static lg:p-0 lg:bg-transparent lg:backdrop-blur-none lg:shadow-none lg:border-none lg:z-auto">
      <div className="max-w-4xl mx-auto lg:max-w-none flex flex-col sm:flex-row lg:flex-col gap-3">
        <Button
          className="flex-1 min-h-[56px] bg-gradient-to-br from-primary to-primary-container text-white rounded-xl shadow-lg dark:shadow-md"
          onPress={openGoogleMaps}
          isDisabled={!location && !queryName}
        >
          <span className="font-bold tracking-widest uppercase text-sm">{t('openInGoogleMaps')}</span>
          <MapPin size={18} />
        </Button>
        
        {tabelogUrl && (
          <Button
            className="flex-1 min-h-[56px] bg-white text-stone-900 hover:bg-stone-50 dark:bg-stone-900 dark:text-stone-100 dark:hover:bg-stone-800 rounded-xl border border-stone-200 dark:border-stone-800 shadow-sm"
            onPress={openTabelog}
          >
            <span className="font-bold tracking-widest uppercase text-sm">{t('bookOnTabelog')}</span>
            <CalendarClock size={18} />
          </Button>
        )}
      </div>
    </div>
  )
}
